import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../utils/api';
import api from '../utils/api';
import { isEmployee } from '../constants/roles';
import { resetPresenceStore } from '../components/presenceStore';
import { resetCallSession } from '../components/phone/callSessionStore';

const AuthContext = createContext();
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// Portal path prefixes (attorney / cro / partner / company). Tabs on these paths manage their OWN
// auth via portal tokens (attorney_token, etc.) — the CMS AuthContext must not run its admin-session
// logic there, or it clobbers the admin's SHARED localStorage (auth_token/user and activity session).
// Defined once (rule 8); used by both effects below. `(\/|$)` so `/attorneys/apply` does NOT match.
const PORTAL_PATH_RE = /^\/(attorney|cro|partner|company)(\/|$)/;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [sessionLocked, setSessionLocked] = useState(false);
  const _refreshing = useRef(false);
  const activitySessionId = useRef(null);
  const timeclockEntryId = useRef(null);
  const lastActivityForLock = useRef(Date.now());
  const sessionLockFiredRef = useRef(false);
  const [pendingIdlePeriod, setPendingIdlePeriod] = useState(null);
  const openIdleCheckedRef = useRef(false);
  // J4 (2026-09-28) — true while this tab is an impersonation session. Every employee time/activity
  // machine (clock-in prompt, idle tracker/modal, presence selector, work-time pings, activity session,
  // exit-disposition popup) is gated OFF on this so an impersonation session never writes hours/activity
  // for the target. Consumed here (effects) and by the popups via useAuth().
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Silently refresh the access token and return true on success
  const silentRefresh = async () => {
    if (_refreshing.current) return false;
    _refreshing.current = true;
    try {
      const res = await api.post('/auth/refresh');
      if (res.data?.access_token) {
        localStorage.setItem('auth_token', res.data.access_token);
        _refreshing.current = false;
        return true;
      }
    } catch (e) {
      // ignore
    }
    _refreshing.current = false;
    return false;
  };

  useEffect(() => {
    // Portal tabs (attorney / cro / partner / company) — including the one-time impersonation
    // entry opened by portals_impersonation_api (window.open('/attorney?impersonate=...')) — manage
    // their OWN auth via portal tokens (attorney_token, etc.). This CMS AuthContext must do NOTHING
    // here on such a tab: running silentRefresh/getCurrentUser/logout would use the portal token
    // (utils/api.js keys the bearer off window.location.pathname) and then clobber the admin's SHARED
    // localStorage auth_token/user — the impersonation session-loss defect. Detect it SYNCHRONOUSLY
    // from the URL (not a flag the flow sets): AttorneyDashboard is lazy-loaded, so this effect runs
    // before useImpersonation could set any flag — a URL check is the only race-safe signal, and it
    // covers BOTH race branches (attorney_token written or not). Admin (/admin) pages are unaffected,
    // so the impersonation_api.py / ImpersonateButton flow (which stays on /admin) keeps working.
    if (PORTAL_PATH_RE.test(window.location.pathname)) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user');
    // Detect impersonation by decoding the JWT payload (no signature check needed here —
    // the backend validates signatures; we just need to know if this is an impersonation session).
    const isImpersonating = (() => {
      if (localStorage.getItem('impersonation_active') !== 'true') return false;
      try {
        const parts = (token || '').split('.');
        if (parts.length !== 3) return false;
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        return !!payload.impersonated;
      } catch {
        return false;
      }
    })();

    // If the flag is stale (token is NOT an impersonation token), clear it immediately.
    if (localStorage.getItem('impersonation_active') === 'true' && !isImpersonating) {
      localStorage.removeItem('impersonation_active');
      localStorage.removeItem('impersonation_name');
      localStorage.removeItem('impersonation_email');
      localStorage.removeItem('admin_backup_token');
      localStorage.removeItem('admin_backup_user');
    }

    if (token && savedUser) {
      const parsed = JSON.parse(savedUser);
      setUser(parsed);
      setMustChangePassword(!!parsed.must_change_password);
      setIsImpersonating(isImpersonating);

      // J3 (2026-09-28) — FULL-VIEW impersonation. Load the TARGET's full user object via /auth/me with
      // the impersonation token (get_current_user returns the full doc — resolving team/collections
      // employees too — plus is_impersonation), so the target's own menus (e.g. Shar's Payroll) render.
      // Do NOT silentRefresh here and never start the activity/idle machinery (J4) — that would write
      // hours/activity for the target. If /auth/me fails, fall back to the trimmed stored user.
      if (isImpersonating) {
        (async () => {
          try {
            const response = await authAPI.getCurrentUser();
            setUser({ ...response.data, is_impersonation: true });
            localStorage.setItem('user', JSON.stringify(response.data));
          } catch { /* keep trimmed stored user */ }
          finally { setLoading(false); }
        })();
        return;
      }

      // Restore activity refs from localStorage on page reload
      activitySessionId.current = localStorage.getItem('activity_session_id');
      timeclockEntryId.current = localStorage.getItem('timeclock_entry_id');

      // Try to proactively refresh the token first, then fetch current user.
      // This prevents the race where a slightly expired token causes an
      // immediate logout on page navigation.
      (async () => {
        try {
          // Attempt silent refresh first — if token is near expiry this extends it
          await silentRefresh();

          const response = await authAPI.getCurrentUser();
          setUser(response.data);
          setMustChangePassword(!!response.data.must_change_password);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (err) {
          // getCurrentUser failed — try one more silent refresh then retry
          const refreshed = await silentRefresh();
          if (refreshed) {
            try {
              const response = await authAPI.getCurrentUser();
              setUser(response.data);
              setMustChangePassword(!!response.data.must_change_password);
              localStorage.setItem('user', JSON.stringify(response.data));
            } catch {
              logout();
            }
          } else {
            logout();
          }
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setLoading(false);
    }
  }, []);

  // Idle detection + heartbeat — locks session after 3 hours (no logout).
  // Employee-facing: a school client (guest_teacher) or a stale non-employee session
  // must never get the activity heartbeat or the PIN session lock. Gate on identity.
  useEffect(() => {
    // J4 — an impersonation session never runs the heartbeat / idle-lock / activity machinery
    // (that would feed the target's activity + hold a session open for them).
    if (!user || !isEmployee(user) || isImpersonating) return;

    const IDLE_TIMEOUT_MS = 3 * 60 * 60 * 1000; // 3 hours → lock session
    const HEARTBEAT_INTERVAL_MS = 60 * 1000;

    const resetActivity = () => {
      if (sessionLockFiredRef.current) return; // don't reset while locked
      lastActivityForLock.current = Date.now();
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetActivity, { passive: true }));

    let _tickCount = 0;
    const interval = setInterval(async () => {
      _tickCount++;
      // Proactively refresh the access token every 10 min regardless of user activity.
      // Access token expires at 35 min; without this, idle users get hard-logged out by
      // the 401 interceptor in api.js before the 1-hour PIN lock ever fires.
      if (_tickCount % 10 === 0) silentRefresh();

      const idleMs = Date.now() - lastActivityForLock.current;
      const sid = activitySessionId.current || localStorage.getItem('activity_session_id');
      const eid = timeclockEntryId.current || localStorage.getItem('timeclock_entry_id');
      const isActive = idleMs < 120000;

      // Heartbeat
      if (sid) {
        api.post(`/activity/heartbeat/${sid}?is_active=${isActive}`).catch(() => {});
      }

      // Lock session after 2 hours idle — does NOT log out, user unlocks with PIN
      // Timeclock continues running while screen is locked; only manual clock-out, logout, or browser close stops it.
      if (idleMs >= IDLE_TIMEOUT_MS && !sessionLockFiredRef.current) {
        sessionLockFiredRef.current = true;
        setSessionLocked(true);
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      events.forEach(e => window.removeEventListener(e, resetActivity));
    };
  }, [user, isImpersonating]);

  // Best-effort cleanup on tab/window close
  useEffect(() => {
    // Portal tabs (incl. the impersonation entry) must not end the ADMIN's activity session on
    // unload — activity_session_id lives in SHARED localStorage, so closing the portal tab would
    // beacon-end the admin's session. Skip on portal paths (same guard as the mount effect).
    if (PORTAL_PATH_RE.test(window.location.pathname)) return;
    const handleUnload = () => {
      const sid = activitySessionId.current || localStorage.getItem('activity_session_id');
      const eid = timeclockEntryId.current || localStorage.getItem('timeclock_entry_id');
      if (sid) {
        navigator.sendBeacon(
          `${BACKEND}/api/activity/session/${sid}/end`,
          new Blob([JSON.stringify({})], { type: 'application/json' })
        );
      }
      // NOTE: Do NOT clock out on beforeunload — that event fires on refresh too,
      // which would clock everyone out every time they navigate. Clock-out only
      // happens via the explicit clock-out button, logout, or the 45-min idle timer.
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // On login/reload: surface any unanswered idle period immediately.
  // The DB record is the source of truth — timeclock state is irrelevant.
  // An open period must be answered regardless of whether the user is clocked in.
  useEffect(() => {
    // J4 — never surface the TARGET's open idle period from an impersonation session.
    if (!user || !isEmployee(user) || isImpersonating) {
      openIdleCheckedRef.current = false; // reset so next login re-runs the check
      return;
    }
    if (openIdleCheckedRef.current) return;
    openIdleCheckedRef.current = true;
    (async () => {
      try {
        const idleRes = await api.get('/audit/idle-period/open');
        if (idleRes.data?.period) {
          setPendingIdlePeriod(idleRes.data.period);
        }
      } catch { /* non-critical */ }
    })();
  }, [user, isImpersonating]);

  const _activityHeaders = (userData) => ({
    'X-User-ID': userData?.id || '',
    'X-User-Email': userData?.email || '',
    'X-User-Name': userData?.full_name || userData?.name || '',
    'X-User-Role': userData?.role || '',
  });

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;

    // Always wipe any leftover impersonation state on a real login
    setIsImpersonating(false);
    localStorage.removeItem('impersonation_active');
    localStorage.removeItem('impersonation_name');
    localStorage.removeItem('impersonation_email');
    localStorage.removeItem('admin_backup_token');
    localStorage.removeItem('admin_backup_user');
    // Clear any stale timeclock/activity state from previous session
    resetPresenceStore(); // a fresh login re-reads presence from the backend, never the prior user's mirror
    resetCallSession();   // never inherit a prior user's active-call panel on this tab
    localStorage.removeItem('timeclock_entry_id');
    localStorage.removeItem('activity_session_id');

    localStorage.setItem('auth_token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('credlocity_lang', userData.preferred_language === 'es' ? 'es' : 'en');
    window.dispatchEvent(new CustomEvent('credlocity-lang-change', {
      detail: { lang: userData.preferred_language === 'es' ? 'es' : 'en' },
    }));
    if (userData.session_id) {
      sessionStorage.setItem('audit_session_id', userData.session_id);
    }

    // Force clock-in prompt for all non-exempt employees. A school client
    // (guest_teacher) is never prompted to clock in.
    if (isEmployee(userData) && !userData.is_exempt_from_timeclock) {
      try {
        const todayRes = await api.get('/activity/timeclock/today', {
          headers: { 'X-User-ID': userData.id || userData.email || '' },
        });
        if (!todayRes.data?.is_clocked_in) {
          localStorage.setItem('requires_clockin', '1');
        }
      } catch { /* non-critical */ }
    }

    setUser(userData);
    setMustChangePassword(!!userData.must_change_password);

    // Start activity session — employees only. A school client (guest_teacher) is not
    // tracked, so no user_sessions/activity row is ever opened for them.
    try {
      if (isEmployee(userData)) {
        const headers = _activityHeaders(userData);
        const sessionRes = await api.post('/activity/session/start', {}, { headers });
        const sid = sessionRes.data?.session_id;
        if (sid) {
          localStorage.setItem('activity_session_id', sid);
          activitySessionId.current = sid;
        }
      }
    } catch (e) { /* non-critical */ }

    return userData;
  };

  const clearForcePassword = () => {
    setMustChangePassword(false);
    if (user) {
      const updated = { ...user, must_change_password: false };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
    }
  };

  const updateUser = (updatedData) => {
    const merged = { ...user, ...updatedData };
    setUser(merged);
    localStorage.setItem('user', JSON.stringify(merged));
  };

  const logout = async () => {
    // End activity session
    const sid = activitySessionId.current || localStorage.getItem('activity_session_id');
    if (sid) {
      try { await api.post(`/activity/session/${sid}/end`); } catch (e) { /* non-critical */ }
    }

    // Auto clock-out on logout is DISABLED (Phase 0 hotfix, 2026-08-06).
    // ce3caa7 (2026-08-05) corrected a double-/api prefix that had kept this path dead
    // and 404'ing silently since May; reviving it meant every logout() fired a real
    // clock-out. logout() is called not only by the explicit Sign Out button but also
    // automatically on token-restore failure (page reload) and by admin force-logout —
    // and with the team's required duplicate-tab workflow those auto-logouts were
    // silently closing shifts mid-work while the rep kept going in another tab.
    // Clock-out now happens ONLY via the explicit dashboard Clock Out buttons. The
    // timeclock entry is intentionally left OPEN here; a forgotten clock-out is safely
    // handled by the ghost sweeper (auto_close_ghost_sessions) + needs_review + the
    // run_payroll hard block — never a silent early close.

    if (window.__auditRecordLogout) {
      window.__auditRecordLogout('manual').catch(() => {});
    }
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('audit_session_id');
    localStorage.removeItem('activity_session_id');
    localStorage.removeItem('timeclock_entry_id');
    activitySessionId.current = null;
    timeclockEntryId.current = null;
    // Clear the session lock so it doesn't overlay the login page.
    // The idle timer sets sessionLockFiredRef=true before calling setSessionLocked(true).
    // logout() previously never reset either, so after "Sign out instead" the full-screen
    // overlay stayed on top of /admin/login — blocking the form entirely.
    sessionLockFiredRef.current = false;
    setSessionLocked(false);
    sessionStorage.removeItem('idle_reason_submitted');
    resetPresenceStore(); // clear the shared presence mirror so the next user on this tab starts fresh
    resetCallSession();   // drop any active-call panel so the next user on this tab starts fresh
    setUser(null);
    setMustChangePassword(false);
  };

  const clearIdleFlag = () => {
    sessionStorage.removeItem('idle_clockout');
  };

  const unlockSession = useCallback(() => {
    lastActivityForLock.current = Date.now();
    sessionLockFiredRef.current = false;
    sessionStorage.removeItem('idle_reason_submitted');
    setSessionLocked(false);
  }, []);

  const clearPendingIdlePeriod = useCallback(() => setPendingIdlePeriod(null), []);

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isImpersonating,
    mustChangePassword,
    clearForcePassword,
    updateUser,
    clearIdleFlag,
    sessionLocked,
    unlockSession,
    pendingIdlePeriod,
    clearPendingIdlePeriod,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
